import { Add, GroupOutlined } from '@mui/icons-material'
import { Alert, Box, Button, Snackbar, Tab, TablePagination, Tabs } from '@mui/material'
import axios from 'axios'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useUser } from '../hooks'
import NotesDrawer, { type NoteItem } from '../components/notes/NotesDrawer'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { SupplierManufacturersDrawer } from './SupplierManufacturersDrawer'
import { useDrawerState } from '../components/shared/useDrawerState'
import { EditSupplierDrawer } from './EditSupplierDrawer'
import { NewSupplierRequestDialog } from './NewSupplierRequestDialog'
import { AssignOemDialog } from './AssignOemDialog'
import { SelectNetsuiteVendorDialog } from './SelectNetsuiteVendorDialog'
import { SupplierFilters } from './SupplierFilters'
import { SupplierTable } from './SupplierTable'
import { OemRequestTable } from './OemRequestTable'
import { useManufacturerData } from '../ManufacturerList/useManufacturerData'
import { useSupplierData } from './useSupplierData'
import { useOemRequestData } from './useOemRequestData'
import '../components/styles/ListPage.css'
import type { ManufacturerRow } from '../ManufacturerList/ManufacturerList.types'
import type { NewSupplierDraft, OemRequestRow, OemRequestStatus, SupplierRow, SupplierTab } from './SupplierList.types'
import ErrorScreen from 'src/Misc/ErrorScreen'

export default function SupplierList() {
  const user = useUser()
  const [searchParams] = useSearchParams()

  const [activeTab, setActiveTab] = useState<SupplierTab>('approved')
  const { rows, setRows, loading, error, filters, setFilters, page, setPage, pageSize, setPageSize, totalCount } =
    useSupplierData(activeTab)
  const { rows: manufacturerRows } = useManufacturerData()

  const {
    rows: oemRequestRows,
    setRows: setOemRequestRows,
    loading: oemRequestLoading,
    error: oemRequestError,
    totalCount: oemRequestCount,
  } = useOemRequestData(activeTab, page, pageSize)

  const isOemTab = activeTab === 'oemsPending'
  const tableError = isOemTab ? oemRequestError : error
  const tableCount = isOemTab ? oemRequestCount : totalCount

  const notesDrawer = useDrawerState()
  const authorizedOemDrawer = useDrawerState()
  const newSupplierDrawer = useDrawerState()
  const assignOemDrawer = useDrawerState()
  const editDrawer = useDrawerState()

  const [approvalLoading, setApprovalLoading] = useState(false)
  const [requestLoading, setRequestLoading] = useState(false)
  const [assignOemLoading, setAssignOemLoading] = useState(false)
  const [assignOemSupplierId, setAssignOemSupplierId] = useState<string | null>(null)
  const [drawerReloadKey, setDrawerReloadKey] = useState(0)
  const [activeRowId, setActiveRowId] = useState<string | null>(null)
  const [editRowId, setEditRowId] = useState<string | null>(null)
  const [notesMap, setNotesMap] = useState<Partial<Record<string, NoteItem[]>>>({})
  const [approveTarget, setApproveTarget] = useState<SupplierRow | null>(null)
  const [netsuiteDialogOpen, setNetsuiteDialogOpen] = useState(false)

  useEffect(() => {
    document.title = 'Suppliers List | I-Quote'
  }, [])

  useEffect(() => {
    const supplierName = searchParams.get('supplierName')
    if (supplierName) {
      setFilters(prev => ({ ...prev, supplier: supplierName }))
    }
  }, [searchParams])

  const activeNotes = useMemo(() => {
    if (activeRowId === null) return []
    return notesMap[activeRowId] ?? []
  }, [activeRowId, notesMap])

  const selectedSupplier = useMemo(() => {
    if (editRowId == null) return null
    return rows.find(r => r.id === editRowId) ?? null
  }, [editRowId, rows])

  const oemOptions = useMemo(() => {
    return manufacturerRows.map(r => r.supplier).sort()
  }, [manufacturerRows])

  const activeRowPendingOems = useMemo(() => {
    if (activeRowId === null || activeTab !== 'pending') return undefined
    const pendingRow = rows.find(r => r.id === activeRowId)
    return pendingRow?.authorizedOems
  }, [activeRowId, rows, activeTab])

  const handleTabChange = (_e: React.SyntheticEvent, value: SupplierTab) => {
    setActiveTab(value)
    setFilters(f => ({ ...f, supplier: '', type: '', blanket: false }))
    setActiveRowId(null)
    notesDrawer.close()
    authorizedOemDrawer.close()
    setPage(0)
  }

  const handleNetsuiteVendorProceed = async (netsuiteVendor: string) => {
    if (!approveTarget) return
    try {
      setApprovalLoading(true)
      await axios.put<{ id: string }>(`/supplier/${approveTarget.id}`, {
        name: approveTarget.name,
        type: approveTarget.type,
        blanket: approveTarget.blanket,
        documentsLabel: approveTarget.documentsLabel || 'Link',
        documentsUrl: approveTarget.documentsUrl || '',
        authorizedOems: approveTarget.authorizedOems,
        status: 'approved',
        defaultNetsuiteVendor: netsuiteVendor,
      })
      setRows(prev => prev.filter(r => r.id !== approveTarget.id))
      setNetsuiteDialogOpen(false)
      setApproveTarget(null)
    } catch (err) {
      console.error('Failed to create supplier in DB', err)
      setErrorSnackbar({ open: true, message: 'Failed to approve supplier. Please try again.' })
    } finally {
      setApprovalLoading(false)
    }
  }

  const handleAddNewSupplier = (draft: NewSupplierDraft) => {
    setRequestLoading(true)
    const newRow: Omit<SupplierRow, 'id'> = {
      name: draft.name.trim(),
      type: draft.type.trim(),
      blanket: draft.blanket,
      documentsLabel: 'Link',
      documentsUrl: '',
      authorizedOems: draft.requestedOems,
      status: 'pending',
      defaultNetsuiteVendor: draft.defaultNetsuiteVendor,
    }

    axios
      .post<{ id: string }>('/supplier', newRow)
      .then(res => {
        const addedRow: SupplierRow = {
          ...newRow,
          id: res.data.id,
        }
        setRows(prev => [addedRow, ...prev])
        setActiveTab('pending')
        setFilters(f => ({ ...f, supplier: '', type: '', blanket: false }))
        setPage(0)
        newSupplierDrawer.close()
      })
      .catch(err => {
        console.error('Failed to add new supplier', err)
        setErrorSnackbar({ open: true, message: 'Failed to add new supplier. Please try again.' })
      })
      .finally(() => {
        setRequestLoading(false)
      })
  }

  const handleAssignOem = (supplier: SupplierRow, manufacturer: ManufacturerRow, _loaFile: File | null) => {
    // TODO: send _loaFile with the OEM request
    // LOAs are currently not being stored
    setAssignOemLoading(true)
    axios
      .post<{ id: string }>('/supplier/oem-request', {
        supplierId: supplier.id,
        manufacturerId: manufacturer.id,
        supplierName: supplier.name,
        provider: manufacturer.supplier,
      })
      .then(res => {
        const addedRow: OemRequestRow = {
          id: res.data.id,
          supplierName: supplier.name,
          provider: manufacturer.supplier,
          status: 'pending',
        }
        setOemRequestRows(prev => [addedRow, ...prev])
        if (assignOemSupplierId) {
          setDrawerReloadKey(key => key + 1)
          setSnackbar({ open: true, message: 'OEM request submitted.' })
        } else {
          setActiveTab('oemsPending')
          setPage(0)
        }
        assignOemDrawer.close()
        setAssignOemSupplierId(null)
      })
      .catch(err => {
        console.error('Failed to request OEM', err)
        setErrorSnackbar({
          open: true,
          message:
            (axios.isAxiosError(err) ? err.response?.data?.message : null) ??
            'Failed to request OEM. Please try again.',
        })
      })
      .finally(() => {
        setAssignOemLoading(false)
      })
  }

  const handleSaveOemStatus = async (row: OemRequestRow, status: OemRequestStatus) => {
    try {
      await axios.put(`/supplier/oem-request/${row.id}`, { status })
      if (status === 'approved') {
        setOemRequestRows(prev => prev.filter(r => r.id !== row.id))
        setSnackbar({ open: true, message: 'OEM added to supplier.' })
      } else {
        setOemRequestRows(prev => prev.map(r => (r.id === row.id ? { ...r, status } : r)))
      }
    } catch (err) {
      console.error('Failed to update OEM request', err)
      setErrorSnackbar({ open: true, message: 'Failed to update OEM request. Please try again.' })
    }
  }

  const patchSupplierRow = (id: string, patch: Partial<SupplierRow>) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)))
  }

  const openEditSupplier = (rowId: string) => {
    setEditRowId(rowId)
    editDrawer.open()
  }

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  })
  const [errorSnackbar, setErrorSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  })

  const handleDeleteSupplier = async () => {
    if (!selectedSupplier) return
    try {
      await axios.delete(`/supplier/${selectedSupplier.id}`)
      setRows(prev => prev.filter(r => r.id !== selectedSupplier.id))
      setDeleteConfirmOpen(false)
      editDrawer.close()
      setEditRowId(null)
      setSnackbar({ open: true, message: 'Supplier deleted.' })
    } catch (err) {
      console.error('Failed to delete supplier', err)
      setDeleteConfirmOpen(false)
      setErrorSnackbar({ open: true, message: 'Failed to delete supplier. Please try again.' })
    }
  }

  return (
    <div className="list-page">
      <SupplierFilters filters={filters} onFiltersChange={setFilters} typeOptions={['Distributor', 'Reseller']} />

      {tableError ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="calc(100vh - 102px - 106px)">
          <ErrorScreen title="Error loading suppliers" message={tableError} />
        </Box>
      ) : (
        <div className="list-table-container">
          <div className="list-table-pagination-header">
            <div className="list-table-pagination-header-left">
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab value="approved" label="Approved Suppliers" />
                <Tab value="pending" label="Suppliers Pending Authorization" />
                <Tab value="oemsPending" label="OEMs Pending Assignment" />
              </Tabs>

              <Button variant="contained" size="large" startIcon={<Add />} onClick={newSupplierDrawer.open}>
                Request New
              </Button>
              <Button variant="outlined" size="large" startIcon={<GroupOutlined />} onClick={assignOemDrawer.open}>
                Assign OEM
              </Button>
            </div>

            <TablePagination
              component="div"
              count={tableCount}
              rowsPerPage={pageSize}
              page={page}
              onPageChange={(_event, newPage) => setPage(newPage)}
              onRowsPerPageChange={event => setPageSize(parseInt(event.target.value, 10))}
            />
          </div>

          {isOemTab ? (
            <OemRequestTable
              rows={oemRequestRows}
              loading={oemRequestLoading}
              pageSize={pageSize}
              onSaveStatus={handleSaveOemStatus}
            />
          ) : (
            <SupplierTable
              rows={rows}
              activeTab={activeTab}
              loading={loading}
              pageSize={pageSize}
              onEditSupplier={openEditSupplier}
              onOpenAuthorizedOem={rowId => {
                setActiveRowId(rowId)
                authorizedOemDrawer.open()
              }}
              onOpenNotes={rowId => {
                setActiveRowId(rowId)
                notesDrawer.open()
              }}
              onApprove={setApproveTarget}
            />
          )}

          <Box display="flex" justifyContent="flex-end">
            <TablePagination
              component="div"
              count={tableCount}
              rowsPerPage={pageSize}
              page={page}
              onPageChange={(_event, newPage) => setPage(newPage)}
              onRowsPerPageChange={event => setPageSize(parseInt(event.target.value, 10))}
            />
          </Box>
        </div>
      )}

      <SupplierManufacturersDrawer
        open={authorizedOemDrawer.isOpen}
        onClose={() => {
          authorizedOemDrawer.close()
          setActiveRowId(null)
        }}
        supplierId={activeRowId}
        pendingOems={activeRowPendingOems}
        onAssignOem={() => {
          setAssignOemSupplierId(activeRowId)
          assignOemDrawer.open()
        }}
        reloadKey={drawerReloadKey}
      />

      <NotesDrawer
        open={notesDrawer.isOpen}
        onClose={() => {
          notesDrawer.close()
          setActiveRowId(null)
        }}
        header="Notes"
        notes={activeNotes}
        onAddNote={({ notes: noteText }) => {
          if (activeRowId === null) return
          const today = new Date()
          const newNote: NoteItem = {
            id: crypto.randomUUID(),
            dateTime: `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(
              2,
              '0',
            )}/${today.getFullYear()}`,
            user: user.displayName ?? 'Unknown',
            title: 'Update',
            notes: noteText,
          }
          setNotesMap(prev => ({
            ...prev,
            [activeRowId]: [newNote, ...(prev[activeRowId] ?? [])],
          }))
        }}
      />

      <NewSupplierRequestDialog
        open={newSupplierDrawer.isOpen}
        loading={requestLoading}
        onClose={newSupplierDrawer.close}
        onSubmit={handleAddNewSupplier}
        typeOptions={['Distributor', 'Reseller']}
        oemOptions={oemOptions}
      />

      <AssignOemDialog
        open={assignOemDrawer.isOpen}
        loading={assignOemLoading}
        onClose={() => {
          assignOemDrawer.close()
          setAssignOemSupplierId(null)
        }}
        onSubmit={handleAssignOem}
        manufacturerOptions={manufacturerRows}
        defaultSupplierId={assignOemSupplierId}
      />

      <ConfirmDialog
        open={approveTarget !== null && !netsuiteDialogOpen}
        title="Approve Supplier?"
        body="Are you sure you want to approve this supplier?"
        confirmLabel="APPROVE"
        cancelLabel="CANCEL"
        onConfirm={() => setNetsuiteDialogOpen(true)}
        onCancel={() => setApproveTarget(null)}
      />

      <SelectNetsuiteVendorDialog
        open={netsuiteDialogOpen}
        loading={approvalLoading}
        defaultVendor={approveTarget?.defaultNetsuiteVendor ?? ''}
        onCancel={() => {
          setNetsuiteDialogOpen(false)
          setApproveTarget(null)
        }}
        onProceed={handleNetsuiteVendorProceed}
      />

      <EditSupplierDrawer
        open={editDrawer.isOpen}
        onClose={() => {
          editDrawer.close()
          setEditRowId(null)
        }}
        supplier={selectedSupplier}
        typeOptions={['Distributor', 'Reseller']}
        onSave={async (patch: Partial<SupplierRow>) => {
          if (!selectedSupplier) return
          const isApproved = rows.some(r => r.id === selectedSupplier.id)
          if (isApproved) {
            try {
              await axios.put(`/supplier/${selectedSupplier.id}`, {
                name: patch.name ?? selectedSupplier.name,
                type: patch.type ?? selectedSupplier.type,
                blanket: patch.blanket ?? selectedSupplier.blanket,
                documentsUrl: patch.documentsUrl ?? selectedSupplier.documentsUrl,
                defaultNetsuiteVendor: patch.defaultNetsuiteVendor ?? selectedSupplier.defaultNetsuiteVendor,
              })
            } catch (err) {
              console.error('Failed to update supplier', err)
            }
          }
          patchSupplierRow(selectedSupplier.id, patch)
          editDrawer.close()
          setEditRowId(null)
        }}
        onDelete={() => setDeleteConfirmOpen(true)}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Supplier?"
        body="Are you sure you want to delete this supplier? This will also remove it from any linked manufacturers."
        confirmLabel="DELETE"
        cancelLabel="CANCEL"
        onConfirm={handleDeleteSupplier}
        onCancel={() => setDeleteConfirmOpen(false)}
      />

      <Snackbar open={errorSnackbar.open}>
        <Alert variant="filled" severity="error" onClose={() => setErrorSnackbar(s => ({ ...s, open: false }))}>
          {errorSnackbar.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      />
    </div>
  )
}
